import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { setBreadcrump } from '../../../app/organizationReducer';
import { setToastData } from '../../../app/toastReducer';
import { AuthorsCollab } from '../../../assets/components/svgs';
import { HTTP_STATUS } from '../../../http/constants/http.status';
import MenuButtonsPortal from '../../Menu/MenuButtonsPortal';
import BiButton from '../../primitives/buttons/BiButton.primitive';
import Card from '../../shared/cards/Card';
import InputField from '../../shared/Fields/InputField';
import LoaderContainer from '../../shared/loaderContainer/LoaderContainer';

import { cloneDeep, find, isArray, isEmpty, isEqual, uniq } from 'lodash';
import { RouteConstants } from '../../../constants/RouteConstants';
import { fieldIsEmpty, validateNameLength } from '../../../util/validators';
import { AppConstants } from '../../../constants/AppConstants';
import { getFailureMessage } from '../../../util/ErrorUtil';
import ToastNotification from '../../Reusable/ToastNotification';
import PickList, { PICKLIST_TYPES } from '../../shared/Fields/PickList';
import TextAreaField from '../../shared/Fields/TextAreaField';

import {
  getCarePlan,
  createCareplanNote,
  updateCareplanNote,
  getCareplanNote,
  deleteCareplanNote
} from '../../../http/careplan-services';
import {
  setCareplanData,
  setOrgList,
  setNotesData,
  setCareplanConfig,
  setListData
} from '../../../app/carePlanReducer';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from '../../shared/ModalPortal/Modal';
import { ExclamationIcon } from '@heroicons/react/outline';
import { CalendarIcon } from '@heroicons/react/outline';
import {
  fetchDeviceList,
  fetchCustomRoleUserList,
  fetchPractitionerRole,
  getCarePlanOrganizations,
  getCareTeamsList,
  configureCareplanCategories,
  getCareplanListData
} from '../carePlanUtils';
import AuditInfo from '../../Common/AuditInfo';
import DependentSelector from '../DependentSelector';
import { checkFeaturePermission } from '../../../util/permissionUtils';

/**
 * @parentId careplan
 * @manager Create, Read, Update, Delete - Careplan Note
 * @overview Create, Read, Update, Delete - Careplan Note
 *   <section>
 *       <p>
 *           <b>View Note</b><br/>
 *           Clicking on note row item on the notes listing page will take user to view note details page in view only mode. Clicking on <b>Back to Notes list</b> will take the user to note(s) listing page.
 *           Details page also includes audit information such as <i>Created By</i>, <i>Created On</i>, <i>Last Modified By</i> and <i>Last Modified On</i>. Logged-in user should have the careplan-service.careplan.notes.get permission.
 *           <br/><br/>
 *           <b>Edit Note</b><br/>
 *           Editing of Careplan is only possible if the logged in user role API permission has edit permissions. i.e <strong>careplan-service.careplan.notes.patch</strong>. Clicking on <b>Edit</b> button on the admin portal toolbar will change the page into editing mode, which allows users to edit all fields like Title, Intent, Description, Status, Category, Author Category and Author, Contributor Category and Contributor, Add organizations field and clicking on <b>Save</b> button will save the changes. Clicking on <b>Cancel</b> will take the user back to careplan details page in view only mode.
 *           <br/><br/>
 *           <b>Create Note</b><br/>
 *           <b>Add a Note</b> button is only enabled if the logged-in user role API permission has create permission. i.e <strong>careplan-service.careplan.notes.create</strong>
 *           Clicking on <b>Add a Note</b> button on careplan listing page will take user to new careplan creation page. Clicking on <b>Cancel</b> will take the user to careplan nots listing page.
 *           <br/><br/>
 *           User can create new careplan note by entering <b>Note Content</b> mandatory fields and clicking on <b>Save</b> button on the admin portal toolbar. Other fields are optional.
 *       </p> <br/>
 *       <p>
 *          <h4>View/Create/Edit careplan Fields</h4>
 *          Create/View/Edit careplan page will display below fields with following validation constraints.
 *          <br/>
 *          <table>
 *            <tr>
 *               <th>Field Name</th>
 *               <th>Required</th>
 *               <th>Data Type</th>
 *               <th>Field Type</th>
 *            </tr>
 *            <tr>
 *               <td>Author Type</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Dropdown</td>
 *            </tr>
 *             <tr>
 *               <td>Author</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Dropdown</td>
 *            </tr>
 *             <tr>
 *               <td>Author organization</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Dropdown</td>
 *            </tr>
 *             <tr>
 *               <td>Date & Time</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>DateTime Picker</td>
 *            </tr>
 *            <tr>
 *               <td>Author String</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>TextInput</td>
 *            </tr>
 *              <tr>
 *               <td>Note Content</td>
 *               <td>Yes</td>
 *               <td>String</td>
 *               <td>TextArea</td>
 *            </tr>
 *          </table>
 *          <br/>
 *     </p>
 *     </section>
 *     <section>
 *      <h4>Failure Status Codes</h3>
 *       <p>
 *       This section describes Create/Edit notifcate template failure status code information.
 *       <br>
 *       <table>
 *           <tr>
 *               <th>HTTP Status Code</th>
 *               <th>Service Error Code</th>
 *               <th>Error Message</th>
 *           </tr>
 *           <tr>
 *               <td>403</td>
 *               <td>CPS_PERMISSION_DENIED</td>
 *               <td>You do not have permission to view this page.</td>
 *           </tr>
 *           <tr>
 *               <td>400</td>
 *               <td>CPS_ERROR_PARSING</td>
 *               <td>Wrong format of category</td>
 *           </tr>
 *           <tr>
 *               <td>500</td>
 *               <td>CPS_INTERNAL_ERROR</td>
 *               <td>Internal Server Error</td>
 *           </tr>
 *           <tr>
 *               <td>503</td>
 *               <td></td>
 *               <td>Service Unavailable</td>
 *           </tr>
 *       </table>
 *   </p>
 *   </section
 *   <section>
 *      <h4>Dependent System settings, Platform services & Role Permission</h3>
 *      <p>This section describes the list of dependent system settings & platform services required for functioning of create/edit/view careplan page.</p>
 *       <h5>System Settings</h4>
 *       <p>Table lists all the dependent system setting(s) defined in configuration service with either global/organization scope.</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Key</th>
 *               <th>Type</th>
 *               <th>Value</th>
 *               <th>Scope</th>
 *           </tr>
 *           <tr>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h5>Platform Service(s)</h4>
 *      <p>Table lists all the dependent platform service(s) with specific version(s) for create/View/Edit careplan page.</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Service Name</th>
 *               <th>Version</th>
 *           </tr>
 *           <tr>
 *               <td>Careplan Service</td>
 *               <td>1.3.0</td>
 *           </tr>
 *           <tr>
 *               <td>User Management Service</td>
 *               <td>1.6.0</td>
 *           </tr>
 *           <tr>
 *               <td>Configuration Service</td>
 *               <td>1.6.0</td>
 *         </tr>
 *       </table>
 *       <br>
 *      <h5>API Role Permission(s)</h4>
 *      <p>Table lists the required API careplan permissions for creating metadata application page</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Feature</th>
 *               <th>API URL</th>
 *               <th>API Method</th>
 *               <th>API Permission</th>
 *               <th>Required</th>
 *           </tr>
 *           <tr>
 *               <td>Create Careplan Note</td>
 *               <td>/careplans</td>
 *               <td>POST</td>
 *               <td>careplan-service.careplan.notes.create</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Get Careplan Note by ID</td>
 *               <td>/careplans/{careplan-id}/notes/{notes-id}</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.notes.get</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Patch careplan note</td>
 *               <td>/careplans/{careplan-id}/notes/{notes-id}</td>
 *               <td>PATCH</td>
 *               <td>careplan-service.careplan.notes.patch</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Delete Careplan note</td>
 *               <td>/careplans/{careplan-id}/notes/{notes-id}</td>
 *               <td>DELETE</td>
 *               <td>careplan-service.careplan.notes.delete</td>
 *               <td>No</td>
 *           </tr>
 *       </table>
 *   </section>
 *   <section>
 *   </section>
 */

const NoteDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isEdit, setIsEdit] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  const [auditData, setAuditData] = useState({
    createdBy: '',
    updatedBy: '',
    updatedOn: '',
    createdOn: ''
  });

  const careplanData = useAppSelector((state) => state.carePlanData.careplanData);
  const notesData = useAppSelector((state) => state.carePlanData.notesData);
  const carePlanOrgList: any = useAppSelector((state) => state.carePlanData.orgList) || [];
  const deviceList: any = useAppSelector((state) => state.carePlanData.deviceList) || [];

  const careplanConfig: any = useAppSelector((state) => state.carePlanData.careplanConfig) || {};
  const listData: any = useAppSelector((state) => state.carePlanData.listData) || {};

  const [deletePopup, setDeletePopup] = useState<boolean>(false);
  const [formDataClone, setFormDataClone] = useState<any>(null);

  const [formData, setFormData] = useState<any>({
    authorType: [],
    author: [],
    authorOrg: [],
    time: '',
    text: '',
    authorString: ''
  });

  const [formError, setFormError] = useState<any>({
    authorType: '',
    author: '',
    authorOrg: '',
    time: '',
    text: '',
    authorString: ''
  });

  const toastData = useAppSelector((state: any) => state.toast);

  useEffect(() => {
    if (!params?.notesId && params?.id) {
      setIsEdit(true);
      setEditMode(false);
      setIsLoading(false);
    }

    if (params?.id && params?.notesId) {
      setEditMode(true);
    }
  }, []);

  const checkConfiguration = async () => {
    let config = await configureCareplanCategories();
    if (!isEmpty(config)) {
      dispatch(setCareplanConfig(config));
    } else {
      setConfigLoaded(true);
    }
  };

  const setBreadcrumps = () => {
    if (!isEmpty(careplanData)) {
      dispatch(
        setBreadcrump([
          {
            key:
              params?.id && !params?.notesId
                ? RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_CREATE.replace(':id', params.id)
                : params?.id && params?.notesId
                ? RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_VIEW.replace(
                    ':id',
                    params.id
                  ).replace(':notesId', params?.notesId)
                : '',
            label:
              params?.id && !params?.notesId
                ? `${t('T_CARE_PLAN_ADD_NOTE')}`
                : params?.id && params?.notesId
                ? `${t('T_CARE_PLAN_VIEW_NOTE')}`
                : ''
          }
        ])
      );
    }
  };

  useEffect(() => {
    if (!isEmpty(careplanData)) {
      setBreadcrumps();
      if (!params?.notesId) {
        setIsLoading(false);
      }
      if (isEmpty(careplanConfig) && !configLoaded) {
        setIsLoading(true);
        checkConfiguration();
        return;
      }
      if (params?.notesId && isEmpty(notesData)) {
        setIsLoading(true);
        getCareplanNotesData();
      }
      if (params?.notesId && !isEmpty(notesData)) {
        setIsLoading(true);
        populateEditData();
      }
    } else {
      setIsLoading(true);
      getCareplanData();
    }
  }, [careplanData, notesData, careplanConfig, configLoaded]);

  const populateEditData = async () => {
    let payload: any = cloneDeep(formData);

    const {
      text = '',
      time = '',
      authorId = '',
      authorType = '',
      authorOrgId = '',
      createdBy = '',
      createdOn,
      createdByOrg = '',
      updatedBy = '',
      updatedOn,
      authorString
    } = notesData || {};

    payload.text = text;
    payload.time = time;
    payload.authorString = authorString;

    const listDataClone: any = cloneDeep(listData);
    const populateList = async (selection: any = {}) => {
      const { key, apiConfig } = selection || {};
      const data = await getCareplanListData(apiConfig);
      if (data.length) {
        listDataClone[key] = data;
      }
    };

    if (authorType?.length) {
      let authorTypeEle = find(
        careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_NOTES_AUTHOR_TYPES],
        { key: authorType }
      );
      if (authorTypeEle) {
        const { apiConfig } = authorTypeEle || {};
        const { fieldKey = '' } = apiConfig || {};
        if (!listDataClone?.[authorType]?.length) {
          await populateList(authorTypeEle);
        }

        let authorEle = find(listDataClone?.[authorType], { [fieldKey]: authorId });
        if (authorEle) {
          payload.author = [authorEle];
        }
        payload.authorType = [authorTypeEle];
      }
    }

    if (authorOrgId?.length) {
      let orgListClone = cloneDeep(carePlanOrgList);
      if (!orgListClone?.length) {
        let listResponse = await getCarePlanOrganizations();
        orgListClone = listResponse;
        dispatch(setOrgList(listResponse));
      }

      let orgEle = find(orgListClone, { id: authorOrgId });
      payload.authorOrg = [orgEle];
    }

    if (!isEqual(listData, listDataClone)) {
      dispatch(setListData(listDataClone));
    }

    setAuditData({
      createdBy: createdBy,
      updatedBy: updatedBy,
      updatedOn: moment(updatedOn).format(AppConstants.DEFAULT_DATE_FORMAT),
      createdOn: moment(createdOn).format(AppConstants.DEFAULT_DATE_FORMAT)
    });
    setFormData(payload);
    setFormDataClone(payload);
    setIsLoading(false);
  };

  const getCareplanData = async () => {
    async function init() {
      try {
        let response = await getCarePlan(params?.id);
        const { status, data } = response;
        if (HTTP_STATUS.isSuccess(status)) {
          dispatch(setCareplanData(data));
        } else {
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
          setIsLoading(false);
          navigate(-1);
        }
      } catch (e) {
        dispatch(
          setToastData({
            isToastActive: true,
            toastMessage: t('T_ERROR_SOMETHING_WRONG'),
            toastType: 'error'
          })
        );
        setIsLoading(false);
        navigate(-1);
      }
    }
    if (
      (params?.id && !isEmpty(careplanData) && params.id !== careplanData?.id) ||
      (isEmpty(careplanData) && params?.id)
    ) {
      await init();
    }
  };

  const getCareplanNotesData = async () => {
    async function init() {
      try {
        let response = await getCareplanNote(params?.id || '', params?.notesId || '');
        const { status, data } = response;
        if (HTTP_STATUS.isSuccess(status)) {
          dispatch(setNotesData(data));
        } else {
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
          setIsLoading(false);
          navigate(-1);
        }
      } catch (e) {
        dispatch(
          setToastData({
            isToastActive: true,
            toastMessage: t('T_ERROR_SOMETHING_WRONG'),
            toastType: 'error'
          })
        );
        setIsLoading(false);
        navigate(-1);
      }
    }
    if (
      (params?.id && params?.notesId && !isEmpty(notesData) && params.notesId !== notesData?.id) ||
      (isEmpty(notesData) && params?.id && params?.notesId)
    ) {
      await init();
    }
  };

  const deleteNote = async () => {
    setIsLoading(true);
    let response = await deleteCareplanNote(
      params?.id || careplanData?.id,
      params?.notesId || notesData?.id
    );

    setDeletePopup(false);
    if ([HTTP_STATUS.HTTP_CREATED, HTTP_STATUS.HTTP_OK].includes(response.status)) {
      dispatch(
        setToastData({
          toastMessage: t('T_CARE_PLAN_NOTE_DELETED_SUCCESS'),
          isToastActive: true,
          toastType: 'success'
        })
      );
      navigate(-1);
    } else {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: t('T_ERROR_SOMETHING_WRONG'),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const validateFormData = () => {
    let errors: any = {
      authorType: '',
      author: '',
      authorOrg: '',
      time: '',
      text: '',
      authorString: ''
    };
    errors['text'] = fieldIsEmpty(formData.text.trim())
      ? t('T_VALIDATION_INPUT_ERR_EMPTY_MSG')
      : validateNameLength(formData.text.trim(), 4096)
      ? ''
      : `${t('T_VALIDATION_ERR_TEXT_LENGTH_CUSTOM_MSG').replace('${LENGTH}', '4096')}`;

    // errors['author'] =
    //   formData?.authorType.length && !formData?.author.length
    //     ? t('T_CARE_PLAN_AUTHOR_SELECTION_REQUIRED')
    //     : '';

    errors['authorString'] = validateNameLength(formData.authorString.trim(), 255)
      ? ''
      : `${t('T_VALIDATION_ERR_TEXT_LENGTH_CUSTOM_MSG').replace('${LENGTH}', '255')}`;
    setFormError(errors);
    return errors;
  };

  const validator = () => {
    if (disableButton) {
      return disableButton;
    }
    const { text } = formData;
    if (!text.length) {
      return true;
    }

    if (isEqual(formData, formDataClone) && editMode) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (disableButton) {
      setDisableButton(false);
    }
  }, [formData]);

  const savePayload = async () => {
    let errors = validateFormData();

    if (
      Object.keys(errors).length !==
      Object.keys(errors).filter((validateError) => {
        return errors[validateError] === '';
      }).length
    ) {
      setDisableButton(true);
      return;
    }

    setIsLoading(true);
    try {
      let payload: any = {};

      const { authorType, author, authorOrg, time, text, authorString = '' } = formData;

      if (authorType.length) {
        const [selectedType] = authorType;
        const { apiConfig } = selectedType || {};
        const { fieldKey = 'id' } = apiConfig;
        const [selectedAuthor] = author;
        payload.authorType = selectedType?.key || '';
        payload.authorId = selectedAuthor?.[fieldKey];
      }

      if (authorOrg.length) {
        const [authorItem] = authorOrg;
        payload.authorOrgId = authorItem?.id || '';
      }

      if (time.length) {
        payload.time = moment(time).format(AppConstants.CAREPLAN_DATE_FORMAT);
      }
      payload.text = text;
      payload.authorString = authorString;
      if (!editMode) {
        let response = await createCareplanNote(careplanData?.id, payload);
        const { data, status } = response;
        if (HTTP_STATUS.isSuccess(status)) {
          dispatch(
            setToastData({
              toastMessage: t('T_CARE_PLAN_NOTE_CREATED_SUCCESS').replace('%s', careplanData.title),
              isToastActive: true,
              toastType: 'success'
            })
          );
          navigate(
            RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_LIST.replace(':id', params?.id || '')
          );
        } else {
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
        }
      } else {
        let response = await updateCareplanNote(
          params?.id || careplanData?.id,
          params?.notesId || notesData?.id,
          payload
        );
        const { data, status } = response;
        if (HTTP_STATUS.isSuccess(status)) {
          dispatch(
            setToastData({
              toastMessage: t('T_CARE_PLAN_NOTE_UPDATED_SUCCESS').replace('%s', careplanData.title),
              isToastActive: true,
              toastType: 'success'
            })
          );
          setIsLoading(false);
          setIsEdit(false);
        } else {
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
        }
      }
    } catch (e: any) {
      dispatch(
        setToastData({
          toastMessage: t('T_SERVER_ERROR'),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const DateInputField = forwardRef(
    ({ onClick, formError, disabled, placeholder, label, fieldKey, overrideValue }: any, ref) => (
      <InputField
        overrideValue={overrideValue}
        className="w-full"
        type="text"
        id="max-word-count"
        label={label}
        placeholder={placeholder}
        fieldKey={fieldKey}
        required={false}
        formData={formData}
        setFormData={setFormData}
        formError={formError}
        disabled={disabled}
        onClick={onClick}
        ref={ref}
        onBlurhandler={(e: any) => {}}
        handleChange={() => {}}
        CustomIcon={CalendarIcon}
      />
    )
  );

  const setTime = () => {
    const { time } = formData;

    const handleDateChange = (date: any, key: string) => {
      const updatedDate = new Date(date);
      setFormData({ ...formData, [key]: updatedDate.toISOString() });
    };

    return (
      <div className="flex w-full flex-row justify-between">
        <DatePicker
          selected={time ? new Date(time) : null}
          onChange={(date: any) => handleDateChange(date, 'time')}
          fixedHeight
          disabled={!isEdit}
          shouldCloseOnSelect={false}
          showMonthDropdown
          showYearDropdown
          showTimeSelect
          scrollableYearDropdown
          yearDropdownItemNumber={100}
          calendarClassName={'text-base'}
          customInput={
            <DateInputField
              overrideValue={time ? moment(time).format(AppConstants.DEFAULT_DATE_FORMAT) : ''}
              fieldKey="time"
              formError={{ time: '' }}
              label={t('T_CARE_PLAN_NOTES_DATE_TIME')}
            />
          }
        />
      </div>
    );
  };

  const updateFormData = (value: any) => {
    let clonedData = cloneDeep(formData);
    Object.keys(value).forEach((key) => {
      clonedData[key] = value[key];
    });
    setFormData(clonedData);
  };

  const selectedAuthorKey = formData?.authorType?.[0]?.key;

  const fetchDataAndUpdateOptions = async (
    dispatchFunction: Function,
    setOptionsFunction: Function,
    list: Array<any>,
    fetchDataFunction: Function
  ) => {
    if (!list.length) {
      const data = await fetchDataFunction();
      dispatch(dispatchFunction(data));
      setOptionsFunction(data);
    } else {
      setOptionsFunction(list);
    }
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <div className="flex w-4/5 flex-col  overflow-visible px-2 py-3">
        <MenuButtonsPortal>
          <>
            <BiButton
              className={'mr-2 text-primary'}
              type="button"
              onClick={() => {
                if (params.id) {
                  if (isEdit && editMode) {
                    setIsEdit(false);
                  } else {
                    navigate(-1);
                  }
                } else {
                  navigate(-1);
                }
              }}
            >
              {params?.id && editMode && !isEdit ? t('T_CARE_PLAN_NOTES_LIST_BACK') : t('T_CANCEL')}
            </BiButton>

            {isEdit ? (
              <BiButton
                className={'bg-primary text-white'}
                type="button"
                onClick={savePayload}
                disabled={validator()}
              >
                {t('T_SAVE')}
              </BiButton>
            ) : checkFeaturePermission(
                AppConstants.FEATURE_KEYS.CAREPLAN_NOTE_MANAGEMENT_UPDATE,
                false
              ) ? (
              <BiButton
                className={'bg-primary text-white'}
                type="button"
                onClick={() => setIsEdit(true)}
              >
                {t('T_EDIT')}
              </BiButton>
            ) : (
              ''
            )}
          </>
        </MenuButtonsPortal>
        <ToastNotification
          message={toastData.toastMessage}
          isActive={toastData.isToastActive}
          setIsActive={() =>
            dispatch(
              setToastData({
                toastMessage: '',
                isToastActive: false,
                toastType: null
              })
            )
          }
          status={toastData.toastType}
        />

        <Card
          title={t('T_CARE_PLAN_NOTES')}
          icon={AuthorsCollab}
          alt={t('T_CARD_ALT_MESSAGE')}
          height=""
          width=""
        >
          <div className="flex w-full flex-wrap">
            <DependentSelector
              formData={formData}
              setFormData={updateFormData}
              disabled={!isEdit}
              primaryFieldKey="authorType"
              primaryFieldLabel={t('T_CARE_PLAN_AUTHOR_TYPE')}
              secondaryFieldKey="author"
              secondaryFieldLabel={t('T_CARE_PLAN_AUTHOR')}
              formError={formError}
              setFormError={setFormError}
              primaryFieldOptions={
                careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_NOTES_AUTHOR_TYPES]
              }
            ></DependentSelector>
            <Modal
              showCloseIcon={true}
              width="48rem"
              headerText={t('T_DELETE_CONFIRMATION_TITLE')}
              toggleVisiblity={(value: boolean) => {
                setDeletePopup(value);
              }}
              isVisible={deletePopup}
              body={
                <div>
                  <div>{t('T_CARE_PLAN_NOTE_DELETE_PROMPT')}</div>
                  <div className="mt-8 flex">
                    <BiButton
                      className={'ml-auto mr-2 text-primary'}
                      type="button"
                      onClick={() => {
                        setDeletePopup(false);
                      }}
                    >
                      {t('T_CANCEL')}
                    </BiButton>
                    <BiButton
                      className={'bg-primary text-white'}
                      type="button"
                      onClick={() => {
                        deleteNote();
                      }}
                    >
                      {t('T_DELETE')}
                    </BiButton>
                  </div>
                </div>
              }
              headerLeftIcon={<ExclamationIcon className="h-7.5 w-6" />}
            />
            <div className="flex w-full flex-wrap">
              <div className="mb-5 flex w-full">
                <div className="flex w-1/2 flex-col">
                  <PickList
                    fieldKey={'id'}
                    fieldValues={['name']}
                    label={t('T_CARE_PLAN_AUTHOR_ORGANIZATION')}
                    selectedItems={formData.authorOrg}
                    onSelectedChange={(items: any) => {
                      setFormData({ ...formData, authorOrg: items });
                    }}
                    items={carePlanOrgList}
                    disabled={!isEdit}
                    readOnly={false}
                    type={PICKLIST_TYPES.SINGLE}
                  />
                </div>
                <div className="w-1/2 pl-5">{setTime()}</div>
              </div>
            </div>
            <div className="mb-5 flex w-full flex-col flex-wrap">
              <InputField
                fieldKey={'authorString'}
                type={'text'}
                id={'authorString'}
                label={t('T_CARE_PLAN_NOTES_AUTHOR_STRING')}
                placeholder={''}
                required={false}
                formData={formData}
                setFormData={setFormData}
                formError={formError}
                asterixFirst={false}
                disabled={!isEdit}
              ></InputField>
            </div>

            <div className="flex w-full flex-col flex-wrap">
              <TextAreaField
                fieldKey={'text'}
                type={'text'}
                id={'description'}
                label={t('T_CARE_PLAN_NOTES_CONTENT')}
                placeholder={''}
                required
                formData={formData}
                setFormData={setFormData}
                formError={formError}
                asterixFirst
                rows={3}
                disabled={!isEdit}
              ></TextAreaField>
            </div>
          </div>
        </Card>
        <Modal
          showCloseIcon={true}
          width={'50rem'}
          headerText={t('T_CARE_PLAN_DATE_PICKER')}
          toggleVisiblity={(value: boolean) => {}}
          isVisible={false}
          body={setTime()}
          headerLeftIcon={''}
        />

        <div className="border-1 overflow-auto rounded-md border-solid border-gray-300"></div>
      </div>

      <div className="flex w-1/5 flex-col pl-5 pt-5">
        <>
          {editMode && (
            <div className="mt-6">
              <>
                {(auditData?.createdOn || auditData?.createdBy) && (
                  <div>
                    <AuditInfo
                      label={'CREATED'}
                      date={auditData?.createdOn || ''}
                      user={auditData?.createdBy || ''}
                    />
                  </div>
                )}
                {(auditData?.updatedOn || auditData?.updatedBy) && (
                  <div>
                    <AuditInfo
                      label={'LAST_MODIFIED'}
                      date={auditData?.updatedOn || ''}
                      user={auditData?.updatedBy || ''}
                    />
                  </div>
                )}

                {/* {auditData.createdByOrg && (
                  <div className="mt-2 w-3/5 text-sm text-[#595959]">
                    <p>{t('T_CREATED_BY_ORG')}</p>
                    <p>{auditData.createdByOrg}</p>
                  </div>
                )} */}
              </>
            </div>
          )}
          {editMode ? (
            <div className="mt-6">
              <BiButton
                className={
                  'w-60 border border-solid border-red-600 bg-white text-danger hover:bg-red-600 hover:text-white'
                }
                type="button"
                onClick={() => {
                  setDeletePopup(true);
                }}
              >
                {t('T_DELETE')}
              </BiButton>
            </div>
          ) : (
            ''
          )}
        </>
      </div>
    </LoaderContainer>
  );
};

export default NoteDetails;
